/**
 * @module WeeklyMVP
 * @description "Guerrier de la Semaine" — MVP showcase card.
 * Shows current MVP or top contender, plus past MVPs.
 */
import React, { useState, useEffect, memo } from 'react';
import { useLanguage, api } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import { Crown, Flame, Loader2, ChevronDown, ChevronUp, Star, Swords } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const TEAM_EMOJI = { rouge: '🔴', bleu: '🔵' };
const TEAM_COLOR = { rouge: 'text-red-400', bleu: 'text-blue-400' };

const WeeklyMVP = memo(function WeeklyMVP() {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  const fr = language === 'fr';

  useEffect(() => {
    api.get('/clash/mvp')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="bg-card/50 border-border animate-pulse">
        <CardContent className="p-6 h-20 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const mvp = data.current_mvp;
  const contender = data.top_contender;
  const display = mvp || contender;

  if (!display && (!data.past_mvps || data.past_mvps.length === 0)) return null;

  return (
    <Card className="bg-card/50 border-red-500/20 overflow-hidden relative" data-testid="weekly-mvp">
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 via-transparent to-yellow-500/5 pointer-events-none" />

      <CardContent className="p-5 relative space-y-3">
        {display && (
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-1.5 mb-2">
              <Swords className="w-4 h-4 text-red-400" />
              <p className="text-[10px] uppercase tracking-widest text-red-400 font-heading font-bold">
                {mvp ? (fr ? 'Champion de la Semaine' : 'Champion of the Week') : (fr ? 'En course pour le titre' : 'Contending for the title')}
              </p>
              <Swords className="w-4 h-4 text-red-400" />
            </div>

            {/* MVP Badge Image + Avatar */}
            <div className="relative mx-auto w-28 h-20 mb-2">
              <motion.img
                src="/badges/guerrier_semaine.png"
                alt="Champion de la Semaine"
                className="w-full h-full object-contain drop-shadow-[0_0_12px_rgba(239,68,68,0.4)]"
                animate={mvp ? { filter: ['drop-shadow(0 0 8px rgba(239,68,68,0.3))', 'drop-shadow(0 0 20px rgba(245,158,11,0.6))', 'drop-shadow(0 0 8px rgba(239,68,68,0.3))'] } : {}}
                transition={{ duration: 2, repeat: Infinity }}
                data-testid="mvp-badge-image"
              />
              <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-amber-500 flex items-center justify-center text-white text-xs font-black ring-2 ring-card">
                {(display.first_name || display.name || '?')[0].toUpperCase()}
              </div>
            </div>

            <p className="font-heading text-xl uppercase tracking-tight text-foreground">
              {display.first_name || display.name}
            </p>

            <div className="flex items-center justify-center gap-2 mt-1">
              {display.team_id && (
                <span className={`text-sm ${TEAM_COLOR[display.team_id] || 'text-muted-foreground'}`}>
                  {TEAM_EMOJI[display.team_id] || ''} 
                </span>
              )}
              <span className="font-heading text-2xl font-black text-red-400">{display.points}</span>
              <span className="text-xs text-muted-foreground">pts</span>
              {mvp && <Crown className="w-5 h-5 text-yellow-400 fill-yellow-400" />}
              {!mvp && <Flame className="w-5 h-5 text-orange-400" />}
            </div>

            {!mvp && contender && (
              <p className="text-[10px] text-muted-foreground mt-1">
                {fr ? 'Le MVP sera annonce dimanche soir!' : 'MVP will be announced Sunday evening!'}
              </p>
            )}
          </motion.div>
        )}

        {/* Past MVPs */}
        {data.past_mvps && data.past_mvps.length > 0 && (
          <div>
            <button
              onClick={() => setShowPast(!showPast)}
              className="flex items-center gap-1 mx-auto text-[10px] text-muted-foreground hover:text-foreground transition-colors"
              data-testid="mvp-history-toggle"
            >
              <Star className="w-3 h-3" />
              {fr ? `Hall of Fame (${data.past_mvps.length})` : `Hall of Fame (${data.past_mvps.length})`}
              {showPast ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            <AnimatePresence>
              {showPast && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-2 space-y-1.5">
                    {data.past_mvps.map(m => (
                      <div key={m.week_start} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/40 text-xs">
                        <div className="w-6 h-6 rounded-lg bg-red-500/20 flex items-center justify-center text-[10px] font-bold text-red-400">
                          {(m.first_name || '?')[0].toUpperCase()}
                        </div>
                        <span className="flex-1 font-medium text-foreground truncate">{m.first_name}</span>
                        <span className="text-muted-foreground">{TEAM_EMOJI[m.team_id] || ''}</span>
                        <span className="font-bold text-red-400">{m.points} pts</span>
                        <span className="text-[9px] text-muted-foreground">{m.week_start}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default WeeklyMVP;
